import React, { useRef, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';

import { useGetOpportunity } from '~/hooks/apis/offers/useGetOffer';

import { isSalesforceOpportunityId } from '~/utils/validateSalesforceId';

const STEPS = [
  {
    label: 'Salesforce ID',
    title: 'Step 1: Add offer ID from Salesforce',
    helperText: 'Enter the Salesforce offer ID to start the set up flow.',
  },
  {
    label: 'Property',
    title: 'Step 2: Check or create property',
    helperText: 'Property details will be checked and pre-filled from Salesforce.',
  },
  {
    label: 'Rooms',
    title: 'Step 3: Create rooms on property',
    helperText: 'Room details will be pre-filled from Salesforce.',
  },
  {
    label: 'Rate plan',
    title: 'Step 4: Create Flash Rate Plan',
    helperText: 'Rate plan details will be pre-filled with the Flash defaults.',
  },
  {
    label: 'Room rates',
    title: 'Step 5: Apply Rate Plan to every room',
    helperText: 'The new Flash Rate Plan will be applied to each room.',
  },
  {
    label: 'Taxes & fees',
    title: 'Step 6: Taxes and Fees',
    helperText: 'Taxes and fees will be selected from the country rules.',
  },
  {
    label: 'Content',
    title: 'Step 7: Go to Content page',
    helperText: 'Content information will be pulled from the Salesforce offer.',
  },
];

interface Props {
  disabled?: boolean;
  vendorId: string;
}

export default function SetUpFlashOfferSection({ disabled, vendorId }: Props) {
  const [salesforceOfferId, setSalesforceOfferId] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isExitConfirmationOpen, setIsExitConfirmationOpen] = useState(false);
  const [isValidatingOpportunity, setIsValidatingOpportunity] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const validationRunId = useRef(0);
  const trimmedSalesforceOfferId = salesforceOfferId.trim();
  const { refetch: refetchOpportunity } = useGetOpportunity(trimmedSalesforceOfferId);

  const currentStep = STEPS[activeStep];

  function handleOpen() {
    setIsDialogOpen(true);
    setIsExitConfirmationOpen(false);
    setIsValidatingOpportunity(false);
    validationRunId.current += 1;
    setSalesforceOfferId('');
    setErrorMessage(null);
    setActiveStep(0);
  }

  function handleClose() {
    if (activeStep === 0 && !trimmedSalesforceOfferId) {
      handleConfirmClose();
      return;
    }

    setIsExitConfirmationOpen(true);
  }

  function handleCancelClose() {
    setIsExitConfirmationOpen(false);
  }

  function handleConfirmClose() {
    validationRunId.current += 1;
    setIsValidatingOpportunity(false);
    setIsExitConfirmationOpen(false);
    setIsDialogOpen(false);
  }

  function handlePrevious() {
    setErrorMessage(null);
    setActiveStep((prev) => Math.max(prev - 1, 0));
  }

  async function handleNext() {
    if (activeStep === 0 && !isSalesforceOpportunityId(trimmedSalesforceOfferId)) {
      setErrorMessage('Enter a valid Salesforce offer ID.');
      return;
    }

    if (activeStep === 0) {
      const runId = validationRunId.current + 1;
      validationRunId.current = runId;
      setIsValidatingOpportunity(true);
      const { data: opportunity, error } = await refetchOpportunity();

      if (runId !== validationRunId.current) {
        return;
      }

      setIsValidatingOpportunity(false);
      const opportunityVendorId = opportunity?.account_18_digit_id__c;

      if (error || !opportunityVendorId) {
        setErrorMessage('Unable to validate the Salesforce offer ID.');
        return;
      }

      if (opportunityVendorId !== vendorId) {
        setErrorMessage('This Salesforce offer does not belong to this vendor.');
        return;
      }
    }

    setErrorMessage(null);
    setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  }

  return (
    <>
      <Button variant="contained" onClick={handleOpen} disabled={disabled}>
        Set up Flash offer
      </Button>

      <Dialog open={isDialogOpen} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ textAlign: 'center' }}>
          Set up Flash offer
          <IconButton aria-label="Close" onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
            {STEPS.map((step) => (
              <Step key={step.label}>
                <StepLabel>{step.label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Stack spacing={2}>
            <Typography variant="h6">{currentStep.title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {currentStep.helperText}
            </Typography>

            {activeStep === 0 && (
              <TextField
                label="Salesforce offer ID"
                value={salesforceOfferId}
                onChange={(event) => {
                  setSalesforceOfferId(event.target.value);
                  setErrorMessage(null);
                }}
                required
                fullWidth
                autoFocus
              />
            )}

            {activeStep !== 0 && (
              <Alert severity="info">Placeholder only. This tab will show the pre-filled information for review.</Alert>
            )}

            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button onClick={handlePrevious} disabled={activeStep === 0}>
            Previous
          </Button>
          <Stack direction="row" spacing={1}>
            <Button onClick={handleClose}>Close</Button>
            {activeStep === STEPS.length - 1 ? (
              <Button variant="contained" onClick={handleConfirmClose}>
                Complete
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={(activeStep === 0 && !trimmedSalesforceOfferId) || isValidatingOpportunity}
              >
                {isValidatingOpportunity ? 'Checking...' : 'Next'}
              </Button>
            )}
          </Stack>
        </DialogActions>
      </Dialog>

      <Dialog open={isExitConfirmationOpen} onClose={handleCancelClose}>
        <DialogTitle>Exit Set up Flash offer?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Any information entered in this wizard will be lost and this action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClose}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmClose}>
            Exit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
